import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Clock, TrendingUp, TrendingDown, Star, ChevronRight, Bell, CalendarDays, ArrowLeftRight, Radio, Crown } from "lucide-react";
import { api, countdownParts, formatKickoff, formatRank } from "@/lib/api";
import { useFlags } from "@/lib/useFlags";
import { T } from "@/lib/testIds";
import PlayerAvatar from "@/components/app/PlayerAvatar";
import TeamBadge from "@/components/app/TeamBadge";
import FDRPill from "@/components/app/FDRPill";
import { cn } from "@/lib/utils";

const StatTile = ({ label, value, sub, tone = "default" }) => (
  <div className="totem-card-inner p-3">
    <div className="label-caps mb-1">{label}</div>
    <div className={cn("display-heading text-xl", tone === "green" && "text-totem-green", tone === "red" && "text-totem-red")}>
      {value}
    </div>
    {sub && <div className="text-[11px] text-totem-textDim mt-0.5 mono">{sub}</div>}
  </div>
);

const Countdown = ({ deadline }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const parts = countdownParts(deadline);
  if (!parts) return null;
  if (parts.expired) return <div className="display-heading text-2xl text-totem-red">DEADLINE PASSED</div>;
  const pad = (n) => String(n).padStart(2, "0");
  return (
    <div data-testid={T.homeDeadlineCountdown} className="display-heading text-4xl text-white mono">
      {parts.days > 0 && <span className="text-totem-green">{parts.days}d </span>}
      {pad(parts.hours)}:{pad(parts.minutes)}:{pad(parts.seconds)}{" "}
      <span className="text-totem-green text-base tracking-wider">REMAINING</span>
    </div>
  );
};

export default function Home() {
  const flags = useFlags();
  const { data, isLoading, error } = useQuery({
    queryKey: ["home"],
    queryFn: async () => (await api.get("/home")).data,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  if (isLoading) {
    return (
      <div className="space-y-4 pt-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="totem-card h-32 animate-pulse" />
        ))}
      </div>
    );
  }
  if (error) {
    return (
      <div className="totem-card p-6 text-sm text-totem-red">
        Could not load dashboard. {String(error?.response?.data?.detail || error.message)}
      </div>
    );
  }

  const gw = data.gameweek;
  const captain = data.captain;
  const live = data.live_score;

  return (
    <div data-testid={T.homeDashboard} className="space-y-4 pt-1 animate-fade-up">
      {/* Deadline card */}
      <section className="totem-card p-5 relative overflow-hidden">
        <div className="flex items-start justify-between">
          <div>
            <div className="label-caps mb-2 text-totem-textDim">
              {gw.name} deadline
            </div>
            <Countdown deadline={gw.deadline_time} />
          </div>
          <Clock className="text-totem-green/60" size={32} strokeWidth={1.5} />
        </div>
        <div className="flex gap-3 mt-4">
          <button
            className="mono uppercase text-xs font-semibold text-totem-bg bg-totem-green px-4 py-2 rounded-lg flex items-center gap-2 hover:brightness-110"
            onClick={() => {
              const d = new Date(gw.deadline_time);
              const cal = `https://calendar.google.com/calendar/r/eventedit?text=FPL+${encodeURIComponent(gw.name)}+deadline&dates=${d.toISOString().replace(/[-:]|\.\d{3}/g, "")}/${d.toISOString().replace(/[-:]|\.\d{3}/g, "")}`;
              window.open(cal, "_blank");
            }}
          >
            <Bell size={14} /> Set reminder
          </button>
          <Link
            to="/fixtures"
            className="mono uppercase text-xs font-semibold text-totem-text bg-totem-surfaceHigh border border-totem-border px-4 py-2 rounded-lg flex items-center gap-2 hover:border-totem-green/60"
          >
            <CalendarDays size={14} /> Full schedule
          </Link>
        </div>
      </section>

      {/* Live score card */}
      <section className="totem-card p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="label-caps">
            {gw.is_current ? `${gw.name} live` : `${gw.name} preview`}
          </div>
          <div className="flex items-center gap-1.5 text-totem-green mono text-[11px] uppercase tracking-wider">
            <TrendingUp size={14} /> Live rank
          </div>
        </div>
        <div className="flex items-end justify-between">
          <div>
            <div data-testid={T.homeLiveScore} className="display-heading text-6xl text-totem-green leading-none">
              {live ?? "—"}
            </div>
            {data.bench_score !== null && data.bench_score !== undefined && (
              <div className="text-xs text-totem-textDim mt-2 mono">+{data.bench_score} on bench</div>
            )}
          </div>
          <div className="text-right">
            <div data-testid={T.homeLiveRank} className="display-heading text-xl text-white">
              {formatRank(data.manager.summary_overall_rank)}
            </div>
            <div className="mono text-[10px] uppercase text-totem-textDim mt-1">
              {data.manager.summary_overall_points ?? 0} pts total
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 mt-4">
          <StatTile
            label="Avg score"
            value={gw.average_entry_score ?? "—"}
            sub={gw.highest_score ? `top ${gw.highest_score}` : ""}
          />
          <StatTile label="Chip" value={data.active_chip || "None"} />
          <StatTile
            label="Team"
            value={data.manager.name || "—"}
            sub={data.manager.player_first_name}
          />
        </div>
      </section>

      {/* Captain watch */}
      {captain && (
        <Link
          to={`/players/${captain.id}`}
          data-testid={T.homeCaptainCard}
          className="totem-card p-5 block hover:border-totem-gold/40 transition-colors"
        >
          <div className="flex items-center justify-between mb-3">
            <span className="inline-block bg-totem-gold/15 text-totem-gold mono uppercase text-[10px] tracking-widest font-semibold px-2.5 py-1 rounded-full border border-totem-gold/30">
              Captain watch
            </span>
            <Star className="text-totem-gold" fill="currentColor" size={18} />
          </div>
          <div className="flex items-center gap-4">
            <PlayerAvatar player={captain} size={64} showRing />
            <div>
              <div className="display-heading text-2xl">{captain.web_name}</div>
              <div className="mono text-xs text-totem-textDim uppercase mt-1">
                {captain.team_short} • {captain.position}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-4 text-sm">
            <div className="flex justify-between">
              <span className="text-totem-textDim">Form</span>
              <span className="mono text-totem-green">{captain.form}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-totem-textDim">Total pts</span>
              <span className="mono">{captain.total_points}</span>
            </div>
          </div>
        </Link>
      )}

      {/* Team tools shortcuts */}
      {(flags.isVisible("live_rank_tracker") || flags.isVisible("player_compare") || flags.isVisible("rank_gap")) && (
        <section className="grid grid-cols-3 gap-3">
          {flags.isVisible("live_rank_tracker") && (
            <Link
              to="/live"
              className="totem-card p-4 flex flex-col justify-between hover:border-totem-red/40 transition group"
            >
              <div className="flex items-center gap-1">
                <Radio className="text-totem-red animate-pulse" size={18} />
                {flags.isPremium("live_rank_tracker") && <Crown className="text-totem-gold" size={12} />}
              </div>
              <div>
                <div className="display-heading text-base leading-tight">Live</div>
                <div className="mono text-[10px] uppercase text-totem-textDim mt-1">Rank tracker</div>
              </div>
            </Link>
          )}
          {flags.isVisible("player_compare") && (
            <Link
              to="/compare"
              className="totem-card p-4 flex flex-col justify-between hover:border-totem-gold/40 transition group"
            >
              <div className="flex items-center gap-1">
                <ArrowLeftRight className="text-totem-gold" size={18} />
                {flags.isPremium("player_compare") && <Crown className="text-totem-gold" size={12} />}
              </div>
              <div>
                <div className="display-heading text-base leading-tight">Compare</div>
                <div className="mono text-[10px] uppercase text-totem-textDim mt-1">Head-to-head</div>
              </div>
            </Link>
          )}
          {flags.isVisible("rank_gap") && (
            <Link
              to="/rank-gap"
              className="totem-card p-4 flex flex-col justify-between hover:border-totem-green/40 transition group"
            >
              <div className="flex items-center gap-1">
                <TrendingUp className="text-totem-green" size={18} />
                {flags.isPremium("rank_gap") && <Crown className="text-totem-gold" size={12} />}
              </div>
              <div>
                <div className="display-heading text-base leading-tight">Rank Gap</div>
                <div className="mono text-[10px] uppercase text-totem-textDim mt-1">Chase tiers</div>
              </div>
            </Link>
          )}
        </section>
      )}

      {/* Upcoming fixtures */}
      <section>
        <div className="flex items-center justify-between mb-2 px-1">
          <div className="label-caps">Upcoming fixtures</div>
          <Link to="/fixtures" className="text-totem-textDim hover:text-totem-green">
            <ChevronRight size={18} />
          </Link>
        </div>
        <div data-testid={T.homeFixturesRail} className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 snap-x">
          {data.upcoming_fixtures.length === 0 && (
            <div className="totem-card px-4 py-3 text-sm text-totem-textDim">No fixtures scheduled.</div>
          )}
          {data.upcoming_fixtures.map((f) => (
            <div key={f.id} className="min-w-[150px] totem-card p-3 snap-start">
              <div className="mono uppercase text-[10px] text-totem-textDim mb-2">
                {formatKickoff(f.kickoff_time)}
              </div>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5">
                  <TeamBadge teamCode={undefined} teamShort={f.team_h} size={20} />
                  <span className="mono text-xs font-semibold">{f.team_h}</span>
                </div>
                <span className="text-totem-textFaint text-[10px]">v</span>
                <div className="flex items-center gap-1.5">
                  <span className="mono text-xs font-semibold">{f.team_a}</span>
                  <TeamBadge teamCode={undefined} teamShort={f.team_a} size={20} />
                </div>
              </div>
              <div className="flex gap-1 mt-2">
                <FDRPill difficulty={f.team_h_difficulty} label={`H ${f.team_h_difficulty ?? "-"}`} />
                <FDRPill difficulty={f.team_a_difficulty} label={`A ${f.team_a_difficulty ?? "-"}`} />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Trending transfers */}
      <section className="totem-card p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="label-caps">Trending transfers</div>
          <Link to="/players?sort=selected_by_percent" className="text-totem-textDim hover:text-totem-green text-xs mono uppercase">
            View all
          </Link>
        </div>
        <ul data-testid={T.homeTrendingList} className="divide-y divide-totem-borderSoft">
          {data.trending_in.slice(0, 3).map((p) => (
            <li key={"in-" + p.id}>
              <Link to={`/players/${p.id}`} className="flex items-center gap-3 py-2.5">
                <PlayerAvatar player={p} size={36} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{p.web_name}</div>
                  <div className="mono text-[10px] uppercase text-totem-textDim">
                    {p.team_short} • {p.position} • £{p.now_cost.toFixed(1)}m
                  </div>
                </div>
                <div className="text-right">
                  <div className="mono text-totem-green flex items-center justify-end gap-1">
                    <TrendingUp size={14} /> {p.transfers_in_event.toLocaleString()}
                  </div>
                  <div className="text-[10px] text-totem-textDim mono uppercase">In</div>
                </div>
              </Link>
            </li>
          ))}
          {data.trending_out.slice(0, 2).map((p) => (
            <li key={"out-" + p.id}>
              <Link to={`/players/${p.id}`} className="flex items-center gap-3 py-2.5">
                <PlayerAvatar player={p} size={36} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{p.web_name}</div>
                  <div className="mono text-[10px] uppercase text-totem-textDim">
                    {p.team_short} • {p.position} • £{p.now_cost.toFixed(1)}m
                  </div>
                </div>
                <div className="text-right">
                  <div className="mono text-totem-red flex items-center justify-end gap-1">
                    <TrendingDown size={14} /> {p.transfers_out_event.toLocaleString()}
                  </div>
                  <div className="text-[10px] text-totem-textDim mono uppercase">Out</div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
